export const CATCH_DATE_PRESETS_DAYS = [7, 30, 90, 180, 365, 730, 1825];

export function mapSortField(accessor) {
  switch (accessor) {
    case 'trap_name':
      return 'device_name';
    case 'trap_type':
      return 'device_type';
    case 'time':
      return 'timestamp';
    default:
      // The API supports most fields using the same names as the UI accessors.
      // If the backend rejects unknown values, the corresponding column should
      // have sorting disabled.
      if ([
        'id',
        'animal',
        'age',
        'sex',
        'bait',
        'coordinates',
        'notes',
      ].includes(accessor)) {
        return accessor;
      }
      return undefined;
  }
}

export function buildCatchesSortBy(sortBy) {
  const field = mapSortField(sortBy?.id);
  if (!field) {
    return undefined;
  }
  return sortBy?.desc ? `-${field}` : field;
}

export function formatCatchCoordinates(coordinates) {
  const latitude = coordinates?.latitude;
  const longitude = coordinates?.longitude;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return '';
  }
  return `${latitude}, ${longitude}`;
}

export function hasCatchLocation(catchItem) {
  const latitude = catchItem?.latitude ?? catchItem?.coordinates?.latitude;
  const longitude = catchItem?.longitude ?? catchItem?.coordinates?.longitude;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }
  return !(latitude === 0 && longitude === 0);
}

export function normalizeCatch(catchItem) {
  const latitude = catchItem?.coordinates?.latitude;
  const longitude = catchItem?.coordinates?.longitude;
  const coordinates = formatCatchCoordinates(catchItem?.coordinates);
  return {
    catch_id: catchItem?.id,
    trap_id: catchItem?.device?.id,
    trap_name: catchItem?.device?.name,
    trap_type: catchItem?.device?.type,
    time: catchItem?.timestamp,
    animal: catchItem?.animal,
    other_animal: catchItem?.other_animal,
    age: catchItem?.age,
    sex: catchItem?.sex,
    bait: catchItem?.bait,
    latitude,
    longitude,
    coordinates,
    notes: catchItem?.notes,
    images_count: catchItem?.images_count,
    history_id: catchItem?.history_id,
  };
}

export const DEFAULT_CATCH_FILTERS = {
  trapName: '',
  type: '',
  datePeriodDays: 0,
  animal: '',
  age: '',
  sex: '',
  bait: '',
  mapPhotos: 'all',
  comment: '',
};

export function hasActiveCatchesFilters(filters) {
  return Boolean(buildCatchesFilter(filters));
}

export function buildCatchesFilter(filters) {
  const parts = [];

  const trapName = filters?.trapName?.trim();
  if (trapName) {
    parts.push(`device_name[like]=${trapName}`);
  }

  const type = filters?.type?.trim();
  if (type) {
    parts.push(`device_type[eq]=${type}`);
  }

  const datePeriodDays = filters?.datePeriodDays ?? 0;
  if (datePeriodDays > 0) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - datePeriodDays);
    parts.push(`timestamp[gte]=${cutoff.toISOString()}`);
  }

  const animal = filters?.animal?.trim();
  if (animal) {
    parts.push(`animal[eq]=${animal}`);
  }

  const age = filters?.age?.trim();
  if (age) {
    parts.push(`age[eq]=${age}`);
  }

  const sex = filters?.sex?.trim();
  if (sex) {
    parts.push(`sex[eq]=${sex}`);
  }

  const bait = filters?.bait?.trim();
  if (bait) {
    parts.push(`bait[like]=${bait}`);
  }

  const mapPhotos = filters?.mapPhotos ?? 'all';
  if (mapPhotos === 'has_location') {
    parts.push('coordinates[not]=null');
  } else if (mapPhotos === 'no_location') {
    parts.push('coordinates[eq]=null');
  } else if (mapPhotos === 'has_photos') {
    parts.push('images_count[gt]=0');
  } else if (mapPhotos === 'no_photos') {
    parts.push('images_count[eq]=0');
  }

  const comment = filters?.comment?.trim();
  if (comment) {
    parts.push(`notes[like]=${comment}`);
  }

  return parts.join('&');
}

export function parseCatchesFilter(filterString) {
  const normalized = filterString?.trim();
  if (!normalized) {
    return { ...DEFAULT_CATCH_FILTERS };
  }

  const presetDays = [...CATCH_DATE_PRESETS_DAYS];
  const result = { ...DEFAULT_CATCH_FILTERS };

  const parts = normalized.split('&').filter(Boolean);
  let hasLocation = false;
  let noLocation = false;
  let hasPhotos = false;
  let noPhotos = false;

  for (const part of parts) {
    const match = part.match(/^(.+?)\[(.+?)\]=(.+)$/);
    if (!match) continue;

    const field = match[1];
    const condition = match[2];
    const value = match[3];

    if (field === 'device_name' && condition === 'like') {
      result.trapName = value;
    } else if (field === 'device_type' && condition === 'eq') {
      result.type = value;
    } else if (field === 'timestamp' && condition === 'gte') {
      const date = new Date(value);
      if (!Number.isFinite(date.getTime())) continue;

      const daysAgo = Math.max(0, Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000)));
      let best = 0;
      let bestDiff = Number.POSITIVE_INFINITY;
      for (const d of presetDays) {
        const diff = Math.abs(d - daysAgo);
        if (diff < bestDiff) {
          best = d;
          bestDiff = diff;
        }
      }
      // Only treat it as a preset match when it is reasonably close.
      result.datePeriodDays = bestDiff <= 2 ? best : 0;
    } else if (field === 'animal' && condition === 'eq') {
      result.animal = value;
    } else if (field === 'age' && condition === 'eq') {
      result.age = value;
    } else if (field === 'sex' && condition === 'eq') {
      result.sex = value;
    } else if (field === 'bait' && (condition === 'like' || condition === 'eq')) {
      result.bait = value;
    } else if (field === 'coordinates' && condition === 'not' && value === 'null') {
      hasLocation = true;
    } else if (field === 'coordinates' && condition === 'eq' && value === 'null') {
      noLocation = true;
    } else if (field === 'coordinates' && condition === 'like') {
      if (value === '.') {
        hasLocation = true;
      } else if (value === '0, 0') {
        noLocation = true;
      }
    } else if (field === 'images_count') {
      if (condition === 'gt' && value === '0') {
        hasPhotos = true;
      } else if (condition === 'eq' && value === '0') {
        noPhotos = true;
      }
    } else if (field === 'notes' && (condition === 'like' || condition === 'eq')) {
      result.comment = value;
    }
  }

  const activeMapPhotos = [
    hasLocation && 'has_location',
    noLocation && 'no_location',
    hasPhotos && 'has_photos',
    noPhotos && 'no_photos',
  ].filter(Boolean);

  if (activeMapPhotos.length === 1) {
    result.mapPhotos = activeMapPhotos[0];
  }

  return result;
}
